import { defineStore } from "pinia";
import { createClient } from "@/sdk";
import type {
  WebhusetConstantsOrderAgreementPeriodTypeItem,
  WebhusetOrderApiFeaturesCatalogVpsGetResponse_CatalogVpsItemResultRestDto,
} from "~/sdk/generated/models";
import { DefaultApiError } from "@microsoft/kiota-abstractions";

export const useVpsCatalogStore = defineStore("vps-catalog", () => {
  const cartStore = useCartStore();
  const config = useRuntimeConfig();

  const client = createClient(config.public.apiServer);

  const isReady = ref(false);
  const catalog =
    ref<
      WebhusetOrderApiFeaturesCatalogVpsGetResponse_CatalogVpsItemResultRestDto[]
    >();

  watch(
    () => cartStore.coupon,
    () => {
      isReady.value = false;
      console.log("Coupon changed", cartStore.coupon);
    }
  );

  const fetchCatalog = async () => {
    try {
      const vps = await client.catalog.vps.get({
        queryParameters: {
          coupon: cartStore.coupon,
        },
      });

      catalog.value = vps?.products;
      isReady.value = true;
    } catch (err) {
      if (err instanceof DefaultApiError && err.responseStatusCode === 404) {
        await nextTick(() => {
          showError("Ugyldig kupongkode");
        });
      } else {
        console.error("Error loading VPS catalog", err);
        await nextTick(() => {
          showError("En uventet feil oppstod under henting av produkter");
        });
      }
    }
  };

  watch(
    isReady,
    async () => {
      if (!isReady.value) {
        await fetchCatalog();
      }
    },
    {
      immediate: true,
    }
  );

  return {
    isReady,
    catalog,
    getTemplateById: (templateId: number) => {
      return catalog.value?.find((vps) => vps.templateId === templateId);
    },
    getTemplateBySubscriptionId: (subscriptionId: number) => {
      return catalog.value?.find((vps) =>
        vps.subscriptions?.find((sub) => sub?.subscriptionId === subscriptionId)
      );
    },
    getSubscriptionById: (subscriptionId: number) => {
      return catalog.value
        ?.flatMap((vps) => vps.subscriptions)
        .find((sub) => sub?.subscriptionId === subscriptionId);
    },

    getByTemplateIdAndAgreementPeriod: (
      templateId: number,
      agreementPeriod: WebhusetConstantsOrderAgreementPeriodTypeItem
    ) =>
      catalog.value
        ?.find((vps) => vps.templateId === templateId)
        ?.subscriptions?.filter(
          (sub) => sub.agreementPeriodType === agreementPeriod
        ),
  };
});
